import { useCallback } from "react";
import { Button, CheckIcon, Col, Table, TableActionBarRow } from "melco-ui";
import { useTranslation } from "react-i18next";
import {
  DrawerType,
  SharedserviceMelcoModelsDesignListItem,
  Search,
  ViewContext,
} from "melco-shared-logic";
import { useDesigns } from "../../hooks/libraries/designs/useDesigns";
import { designsListColumns } from "../libraries/designs/DesignsList";
import { useBasicSettings } from "../../hooks/settings/useBasicSettings";
import { DisplayDesignSize } from "./DisplayDesignSize";
import { ServiceType } from "../../helper/serviceType";

type ChooseDesignListProps = {
  drawer: DrawerType;
  onDesignSelect: (selectedDesign: SharedserviceMelcoModelsDesignListItem) => void;
  serviceType: ServiceType;
};

export const ChooseDesignList: React.FC<ChooseDesignListProps> = ({
  drawer,
  onDesignSelect,
  serviceType,
}) => {
  const {
    isLoading,
    isFetching,
    data,
    isPreviousData,
    errorComponent,
    onTableConfigChange,
    pagination,
    searchQueryChange,
  } = useDesigns(serviceType, "published");

  const { t } = useTranslation();
  const { closeDrawer, drawerField } = drawer;
  const { isSelected } = drawerField;

  const { settings, isLoading: isMeasurementUnitLoaded } = useBasicSettings();

  const onSelect = useCallback(
    async (selectedDesign: SharedserviceMelcoModelsDesignListItem) => {
      onDesignSelect(selectedDesign);
      closeDrawer();
    },
    [onDesignSelect, closeDrawer]
  );

  const columns = [
    ...designsListColumns(t, ViewContext.Drawer),
    {
      title: t("designs_list.columns.size"),
      key: "size",
      dataIndex: "size",
      render: (_: string, design: SharedserviceMelcoModelsDesignListItem) =>
        isMeasurementUnitLoaded ? null : (
          <DisplayDesignSize
            design={design}
            measurementUnit={settings?.measurement_unit}
          />
        ),
    },
    {
      title: "",
      key: "action",
      dataIndex: "action",
      align: "right" as const,
      render: (_: string, d: SharedserviceMelcoModelsDesignListItem) => {
        return isSelected(d.id) ? (
          <Button type="primary" icon={<CheckIcon />} size="small" disabled>
            {t(
              "designMappings.actions.designConfiguration.chooseDesign.actions.selected"
            )}
          </Button>
        ) : (
          <Button
            type="primary"
            icon={<CheckIcon />}
            size="small"
            onClick={() => onSelect(d)}
          >
            {t(
              "designMappings.actions.designConfiguration.chooseDesign.actions.select"
            )}
          </Button>
        );
      },
    },
  ];

  return (
    <>
      <TableActionBarRow>
        <Col>
          <Search
            onSearchInputChange={(query: string) => searchQueryChange(query)}
          />
        </Col>
      </TableActionBarRow>
      <Table
        showSorterTooltip={false}
        sticky
        loading={isPreviousData ? isFetching : isLoading}
        dataSource={data}
        columns={columns}
        onChange={onTableConfigChange}
        pagination={pagination}
        rowKey="id"
        noDataMessage={t("designs_list.empty")}
        errorComponent={errorComponent}
      />
    </>
  );
};
